/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  VIDEO_SETTINGS,
  VIDEO_SUBTITLES
} from 'types/unacademyIcons/player.types';
import { Spacings } from '@styles/index';
import { localStorageAvailable } from '@utils/localStorage';
import {
  PLAYER_STORAGE_KEYS,
  VIDEO_SETTINGS_OPTIONS
} from '@constants/unacademyIcons/player.constants';
import { useLessonPlayerStore } from '@stores/lessonPlayerStore';
import { emptyObject } from '@constants/empty';
import Typography from '@unicons-base/Typography/Typography';
import UN_ICONS_TYPOGRAPHY_VARIANT from 'types/unacademyIcons/typography.type';
import Check from '@icon/Check';
import ChevronLeft from '@icon/ChevronLeft';
import MainMenuItem from './MainMenuItem';

interface SettingsMenuProps {
  closeDropdown: any;
  popper: any;
  showAudioOption: boolean;
}

type SelectedItemType = { $isSelected?: boolean };

const StyledWrapper = styled.div`
  background-color: var(--color-i-icon-dark-button);
  border-radius: 4px !important;
`;

const MainMenu = styled.ul`
  border-radius: 4px !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${Spacings.SPACING_2B} 0;
  border: 1px solid var(--color-i-icon-dark-supporting);
  background-color: var(--color-i-icon-dark-button);
  width: 100%;
  bottom: 1.5em;
  margin: 0;
  font-family: Arial, Helvetica, sans-serif;
  overflow: auto;
`;

const MenuHeader = styled.div`
  width: 100%;
  padding: 8px 16px 0;
  cursor: pointer;
`;

const MenuOptions = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: var(--color-i-icon-dark-button);
  width: 100%;
  bottom: 1.5em;
  padding: ${Spacings.SPACING_2B} 0 0;
  margin: 0;
  font-family: Arial, Helvetica, sans-serif;
  overflow: auto;
`;

const MenuItem = styled.li`
  position: relative;
  align-items: start;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  width: 100%;
  display: flex !important;
  justify-content: space-between;
  min-width: 252px;
  cursor: pointer;
  margin: 0;
  &:hover {
    opacity: 0.8;
    background: var(--color-i-icon-light-hover);
  }
`;

const StyledCheck = styled(Check)`
  position: absolute;
  left: -4px;
  margin-left: ${Spacings.SPACING_4B};
  margin-top: 2px;
`;

const ItemText = styled(Typography)<SelectedItemType>`
  cursor: pointer;
  margin-bottom: 0px;
  margin-left: ${Spacings.SPACING_4B};
  color: ${({ $isSelected }) =>
    $isSelected ? 'var(--color-i-icon-gold)' : 'white'};
`;

const HeaderText = styled(Typography)`
  margin: 0;
  padding-bottom: ${Spacings.SPACING_4B};
  border-bottom: 1px solid var(--color-i-icon-dark-supporting);
`;

const StyledChevronLeft = styled(ChevronLeft)`
  margin-right: 12px;
  margin-bottom: -3px;
`;

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  closeDropdown,
  popper,
  showAudioOption
}) => {
  const [videoSettings, setVideoSettings] = useState({
    currentSetting: '',
    settingItems: {}
  });
  const { currentSetting, settingItems } = videoSettings || emptyObject;
  const {
    player,
    lessonPlaylist,
    currentVideoSpeed,
    currentVideoQuality,
    currentVideoAudio,
    currentSubtitle,
    setVideoSettings: setLessonSettings
  } = useLessonPlayerStore();
  const videoQuality =
    VIDEO_SETTINGS_OPTIONS[VIDEO_SETTINGS.VIDEO_QUALITY][currentVideoQuality];
  const videoAudio = currentVideoAudio;
  const videoSpeed =
    VIDEO_SETTINGS_OPTIONS[VIDEO_SETTINGS.VIDEO_SPEED][currentVideoSpeed];
  const videoSubtitle = currentSubtitle;
  const switchOffSubtitle =
    VIDEO_SETTINGS_OPTIONS[VIDEO_SETTINGS.SUBTITLES][VIDEO_SUBTITLES.OFF];

  const handleSettingClick = (settingType) => {
    if (popper.current) {
      popper.current.scheduleUpdate();
    }
    let settingItems = VIDEO_SETTINGS_OPTIONS[settingType];
    if (settingType === VIDEO_SETTINGS.SUBTITLES) {
      settingItems = { ...(textTracks || {}) };
    }
    if (settingType === VIDEO_SETTINGS.VIDEO_AUDIO) {
      settingItems = { ...(audioTracks || {}) };
    }
    setVideoSettings({
      currentSetting: settingType,
      settingItems
    });
  };

  const handleSettingsBack = () => {
    if (popper.current) {
      popper.current.scheduleUpdate();
    }
    setVideoSettings({
      currentSetting: '',
      settingItems: {}
    });
  };

  const handleOptionClick = (settingKey) => {
    const newSettings = {
      currentVideoQuality,
      currentVideoAudio,
      currentVideoSpeed,
      currentSubtitle
    };
    const playerState = player.state;
    if (currentSetting === VIDEO_SETTINGS.VIDEO_SPEED) {
      player.playbackRate(settingKey);
      player.setState({
        ...playerState,
        currentVideoSpeed: settingKey
      });
      if (localStorageAvailable()) {
        localStorage.setItem(PLAYER_STORAGE_KEYS.LESSON_SPEED, `${settingKey}`);
      }
      newSettings.currentVideoSpeed = settingKey;
    }
    if (currentSetting === VIDEO_SETTINGS.VIDEO_QUALITY && lessonPlaylist) {
      const currentTime = player.currentTime();
      player.setState({
        ...playerState,
        srcTime: currentTime,
        isCoverLoaderVisible: true
      });
      if (localStorageAvailable()) {
        localStorage.setItem(
          PLAYER_STORAGE_KEYS.LESSON_QUALITY,
          `${settingKey}`
        );
      }
      player.src({ src: lessonPlaylist[settingKey] });

      newSettings.currentVideoQuality = settingKey;
    }
    if (currentSetting === VIDEO_SETTINGS.SUBTITLES) {
      player.setState({
        ...playerState,
        currentSubtitle: settingKey
      });
      if (localStorageAvailable()) {
        localStorage.setItem(
          PLAYER_STORAGE_KEYS.LESSON_SUBTITLE,
          `${settingKey}`
        );
      }
      player.trigger('subtitles');
      newSettings.currentSubtitle = settingKey;
    }
    if (currentSetting === VIDEO_SETTINGS.VIDEO_AUDIO && showAudioOption) {
      player.setState({
        ...playerState,
        currentLanguage: settingKey
      });
      if (localStorageAvailable()) {
        localStorage.setItem(
          PLAYER_STORAGE_KEYS.LESSON_LANGUAGE,
          `${settingKey}`
        );
      }
      player.trigger('languages');
      newSettings.currentVideoAudio = settingKey;
    }
    closeDropdown(currentSetting !== VIDEO_SETTINGS.VIDEO_QUALITY);
    setLessonSettings(newSettings);
  };

  const textTracks = useMemo(() => {
    try {
      const tracks = player.textTracks();
      const textTracks = {};
      for (let index = 0; index < tracks.length; index++) {
        const track = tracks[index];
        if (track.language) textTracks[track.label] = track.label;
      }
      return { ...switchOffSubtitle, ...textTracks };
    } catch {
      return {};
    }
  }, [player, switchOffSubtitle]);

  const audioTracks = useMemo(() => {
    try {
      const tracks = player.audioTracks();
      const audioTracks = {};
      for (let index = 0; index < tracks.length; index++) {
        const track = tracks[index];
        if (track.language) audioTracks[track.label] = track.label;
      }
      return audioTracks;
    } catch {
      return {};
    }
  }, [player]);

  return (
    <StyledWrapper className="">
      {!currentSetting && (
        <MainMenu>
          {showAudioOption && (
            <MainMenuItem
              handleSettingClick={handleSettingClick}
              title="Audio"
              settingKey={VIDEO_SETTINGS.VIDEO_AUDIO}
              settingValue={videoAudio}
            />
          )}
          <MainMenuItem
            handleSettingClick={handleSettingClick}
            title="Subtitles"
            settingKey={VIDEO_SETTINGS.SUBTITLES}
            settingValue={videoSubtitle}
          />
          <MainMenuItem
            handleSettingClick={handleSettingClick}
            title="Video quality"
            settingKey={VIDEO_SETTINGS.VIDEO_QUALITY}
            settingValue={videoQuality}
          />
          <MainMenuItem
            handleSettingClick={handleSettingClick}
            title="Video speed"
            settingKey={VIDEO_SETTINGS.VIDEO_SPEED}
            settingValue={videoSpeed}
          />
        </MainMenu>
      )}
      {currentSetting && (
        <MainMenu>
          <MenuHeader onClick={handleSettingsBack}>
            <HeaderText variant={UN_ICONS_TYPOGRAPHY_VARIANT.BODY1}>
              <StyledChevronLeft
                size="14px"
                strokeWidth="4px"
                aria-hidden="true"
                color="var(--color-i-icon-light-tertiary)"
              />
              {currentSetting}
            </HeaderText>
          </MenuHeader>
          <MenuOptions>
            {Object.entries(settingItems).map(([key, title]: any) => {
              const isSelected =
                [videoSpeed, videoQuality].includes(title) ||
                (currentSetting === VIDEO_SETTINGS.VIDEO_AUDIO &&
                  videoAudio === title) ||
                (currentSetting === VIDEO_SETTINGS.SUBTITLES &&
                  videoSubtitle === title);
              return (
                <MenuItem key={key} onClick={() => handleOptionClick(key)}>
                  <ItemText
                    $isSelected={isSelected}
                    variant={UN_ICONS_TYPOGRAPHY_VARIANT.BODY1}
                  >
                    {title}
                  </ItemText>
                  {isSelected && (
                    <StyledCheck size="16px" color="var(--color-i-icon-gold)" />
                  )}
                </MenuItem>
              );
            })}
          </MenuOptions>
        </MainMenu>
      )}
    </StyledWrapper>
  );
};

SettingsMenu.displayName = 'SettingsMenu';

export default SettingsMenu;
